import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useCallback, useContext } from 'react';

import { ConfigContext } from '@/Contexts/ConfigContext';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { useApprovalForm } from '@/hooks/useApplicationApprovalForm';
import { useApproveApplication } from '@/hooks/useApproveApplication';
import useContact from '@/hooks/useContact';
import { useShippingContacts } from '@/hooks/useShippingContacts';
import { AddressProps } from '@/models/Address';
import { ContactProps } from '@/models/Contact';
import { ProgramNames } from '@/models/Program';
import { UserType } from '@/models/User';
import { getStateByStateId, singleLineAddress } from '@/utils/addressUtil';

import { ApplicationApprovalErrorMessages } from '../common/ApplicationApprovalErrorMessages';
import ReadOnlyContact from '../common/contact_components/ReadOnlyContact';
import { DialogTitleWithClose } from '../common/DialogTitleWithClose';
import { stringHasValue } from '@/utils/stringUtil';

function ApplicationApprovalModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { userType } = useContext(UsersContext);
  const { allStates, contactTypes } = useContext(ConfigContext);
  const { currentApplication, canEdit, updateCurrentApplication, partnerInfo } =
    useContext(CurrentApplicationContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { currentContacts } = useContext(CurrentApplicationContext);
  const { updateApplicationStatus } = useContext(DashboardContext);
  const { getContact, saveContact } = useContact();

  const {
    specialInstructions,
    setSpecialInstructions,
    markSpecialDirty,
    additionalStickers,
    onAdditionalStickersChange,
  } = useApprovalForm({ currentApplication, currentPartner: partnerInfo });

  const {
    shippingContactsNeeded,
    shippingContactType,
    selectedContactId,
    handleSelectChange,
    groupedOptions,
    selectedShippingContact,
    isValidShippingSelection,
    ensureShippingContactId,
  } = useShippingContacts({
    currentApplication,
    contactTypes,
    selectedProgramId,
    currentContacts,
    getContact,
    saveContact,
  });

  const {
    approveButtonText,
    approveButtonEnabled,
    errorMessage,
    isSubmitting,
    approveApplication,
  } = useApproveApplication({
    currentApplication,
    canEdit,
    shippingContactsNeeded,
    selectedContactId,
    additionalStickers,
    specialInstructions,
    ensureShippingContactId,
    isValidShippingSelection,
    updateCurrentApplication,
    updateApplicationStatus,
  });

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const buildShippingContactSelectLabel = useCallback(
    (contact: ContactProps) => {
      const address: AddressProps = {
        streetAddress1: contact?.streetAddress1,
        streetAddress2: contact?.streetAddress2,
        city: contact?.city,
        stateId: contact?.stateId,
        countyId: contact?.countyId,
        countryId: contact?.countryId,
        zipCode: contact?.zipCode,
      };

      const stateName = getStateByStateId(allStates, contact?.stateId ?? '')?.name;

      return `${contact?.firstName} ${contact?.lastName} - ${singleLineAddress(
        address,
        stateName ?? ''
      )}`;
    },
    [allStates]
  );

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitleWithClose onClose={handleClose}>
        Review and Approve Application
      </DialogTitleWithClose>

      <DialogContent>
        <Grid container gap={2}>
          {shippingContactsNeeded &&
            (userType === UserType.StateCoordinator || userType === UserType.Adf) && (
              <Grid size={12}>
                <Typography variant='subtitle2'>
                  Select Shipping Address for Recognition Materials
                </Typography>

                <Select
                  id='shippingContactSelect'
                  value={selectedContactId == null ? '' : String(selectedContactId)}
                  fullWidth
                  onChange={handleSelectChange}
                  disabled={isSubmitting}
                  sx={{ display: 'flex', m: 2, justifySelf: 'center' }}
                >
                  {groupedOptions.length === 0 ? (
                    <MenuItem disabled>No shipping contacts available</MenuItem>
                  ) : (
                    groupedOptions.flatMap(([group, contacts]) => [
                      <ListSubheader key={`${group}-header`}>{group}</ListSubheader>,
                      ...contacts.map((c) => (
                        <MenuItem key={c.id ?? 0} value={c.id ?? 0}>
                          {buildShippingContactSelectLabel(c)}
                        </MenuItem>
                      )),
                    ])
                  )}
                </Select>

                {selectedShippingContact != null && (
                  <Box mt={2}>
                    <Typography variant='subtitle2'>
                      Selected Shipping Address for Recognition Materials:
                    </Typography>
                    <Card sx={{ p: 2, m: 2 }}>
                      <ReadOnlyContact
                        contact={selectedShippingContact}
                        contactType={shippingContactType}
                        programId={currentApplication.programId}
                      />
                    </Card>
                  </Box>
                )}
              </Grid>
            )}

          {userType === UserType.Adf && (
            <Grid size={12}>
              {selectedProgramId === ProgramNames.TreeCityUSA && (
                <TextField
                  variant='outlined'
                  label='Request Additional Sign Stickers'
                  helperText='Enter number of additional stickers, leave blank if none'
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 1000,
                  }}
                  value={additionalStickers ?? ''}
                  id='additional-stickers'
                  name='additional-stickers'
                  onChange={onAdditionalStickersChange}
                  disabled={isSubmitting}
                />
              )}

              <Box mt={2}>
                <Typography variant='subtitle2'>Special Instructions</Typography>
                <TextField
                  sx={{ mt: 2, mb: 2 }}
                  id='specialInstructions'
                  value={specialInstructions}
                  onFocus={(e) => (e.target as HTMLInputElement).select()}
                  onChange={(e) => {
                    markSpecialDirty();
                    setSpecialInstructions(e.target.value);
                  }}
                  fullWidth
                  multiline
                  minRows={4}
                  disabled={isSubmitting}
                />
              </Box>
            </Grid>
          )}
        </Grid>

        {stringHasValue(errorMessage) && (
          <ApplicationApprovalErrorMessages errorMessages={[errorMessage]} />
        )}
      </DialogContent>

      <DialogActions>
        <Button
          disabled={!approveButtonEnabled}
          onClick={approveApplication}
          variant='primary'
          size='large'
        >
          {approveButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ApplicationApprovalModal;
